import React, { useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin maintenance page.
 *
 * Self-service "press here to run a one-shot backend task" page.
 * Today it only has the PII encryption backfill button (encrypting any
 * legacy plaintext bank / NI number rows that existed before
 * FIELD_ENCRYPTION_KEY was set).
 *
 * Add new maintenance actions here rather than scattering them across
 * admin dashboards or asking the founder to use curl.
 */
const AdminMaintenancePage = () => {
  const { user, getAuthToken } = useAuth();
  const navigate = useNavigate();
  const isAdmin = (user?.roles || []).includes('admin');

  const [running, setRunning] = useState(false);
  const [result, setResult]   = useState(null);
  const [error, setError]     = useState('');

  const runBackfill = async (execute) => {
    setRunning(true);
    setError('');
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/encrypt-pii-backfill`,
        { execute },
        {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
          withCredentials: true,
        },
      );
      setResult(data);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message || 'Something went wrong';
      if (status === 401 || status === 403) {
        setError('You need to be signed in as an admin to run maintenance tasks.');
      } else if (status === 503) {
        setError(typeof detail === 'string' ? detail : 'FIELD_ENCRYPTION_KEY is not configured on the server.');
      } else {
        setError(String(detail));
      }
    } finally {
      setRunning(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F4F2E9] to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md">
          <p className="text-sm text-gray-700">Please sign in first.</p>
          <button onClick={() => navigate('/login?next=/admin/maintenance')} className="mt-3 w-full bg-[#1F3A68] text-white py-2 rounded-lg">Sign in</button>
        </div>
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F4F2E9] to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md">
          <p className="text-sm text-gray-700">This page is admin only.</p>
          <Link to="/admin/dashboard" className="block mt-3 text-center bg-[#1F3A68] text-white py-2 rounded-lg">Back to admin dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F4F2E9] to-white py-10 px-4">
      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl border border-gray-200 p-6">
        <div className="flex items-start justify-between mb-2">
          <h1 className="text-2xl font-bold text-[#1F3A68]">Admin maintenance</h1>
          <Link to="/admin/dashboard" className="text-xs text-[#1F3A68] hover:underline mt-2">← Back to dashboard</Link>
        </div>
        <p className="text-sm text-gray-600 mb-6">
          Self-service tools for one-off maintenance jobs. Each one is safe to re-run.
        </p>

        {/* ── Encrypt PII backfill ──────────────────────────────────────── */}
        <section className="border border-gray-200 rounded-xl p-5 bg-gray-50">
          <h2 className="text-lg font-bold text-[#1F3A68] mb-1">Encrypt leftover bank / NI data</h2>
          <p className="text-sm text-gray-700 leading-relaxed mb-4">
            When we turned on encryption, all <em>new</em> writes started being encrypted automatically.
            But anything saved <em>before</em> that day is still readable plain text in the database.
            This button finds those leftover rows and encrypts them so they match the new ones.
          </p>
          <p className="text-xs text-gray-500 leading-relaxed mb-4">
            <strong>How to use it:</strong> press <strong>Dry run</strong> first — it tells you how many rows would be encrypted without changing anything.
            If the numbers look right, press <strong>Encrypt now</strong> to apply. Safe to run as many times as you like;
            anything already encrypted is skipped automatically.
          </p>

          {error && (
            <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          {result && (
            <div className={`mb-3 p-3 rounded-lg border text-sm ${
              result.mode === 'EXECUTE'
                ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                : 'bg-blue-50 border-blue-200 text-blue-900'
            }`}>
              <p className="font-semibold mb-1">
                {result.mode === 'EXECUTE' ? '✓ Encrypted' : 'Dry run — nothing changed yet'}
              </p>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left opacity-70">
                    <th className="py-1">Collection</th>
                    <th className="py-1">Field</th>
                    <th className="py-1 text-right">Scanned</th>
                    <th className="py-1 text-right">Would update</th>
                    <th className="py-1 text-right">Encrypted</th>
                  </tr>
                </thead>
                <tbody>
                  {(result.results || []).map((r, i) => (
                    <tr key={i} className="border-t border-current/10">
                      <td className="py-1 font-mono">{r.collection}</td>
                      <td className="py-1">{r.field}</td>
                      <td className="py-1 text-right font-mono">{r.scanned}</td>
                      <td className="py-1 text-right font-mono">{r.to_update}</td>
                      <td className="py-1 text-right font-mono">{r.updated}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => runBackfill(false)}
              disabled={running}
              className="bg-white border border-[#1F3A68] text-[#1F3A68] text-sm font-semibold px-4 py-2 rounded-lg hover:bg-[#1F3A68]/5 disabled:opacity-50"
            >
              {running ? 'Running…' : 'Dry run'}
            </button>
            <button
              onClick={() => {
                if (window.confirm('Encrypt all leftover bank and NI rows now? Safe to re-run.')) {
                  runBackfill(true);
                }
              }}
              disabled={running}
              className="bg-[#1F3A68] text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-[#1F3A68]/90 disabled:opacity-50"
            >
              {running ? 'Running…' : 'Encrypt now'}
            </button>
          </div>
        </section>

        {/* Room for future maintenance buttons */}
      </div>
    </div>
  );
};

export default AdminMaintenancePage;
